import React, { useCallback, useId } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';
import { FacilityPickerV2 } from 'shared/components/FacilityPickerV2';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * Primary transfer fields for create flow (tech spec §6.4). `estimatedDepartureDate` is sent as `YYYY-MM-DD` on `POST /inventory/v1/fc-transfers`.
 *
 * @param {Object} props
 * @param {Record<string, unknown> | null} [props.sourceFacility]
 * @param {Record<string, unknown> | null} [props.destinationFacility]
 * @param {string} [props.notes]
 * @param {string} [props.estimatedDepartureDate] - `YYYY-MM-DD` for `<input type="date" />`
 * @param {(facility: Record<string, unknown> | null) => void} [props.onSourceFacilityChange]
 * @param {(facility: Record<string, unknown> | null) => void} [props.onDestinationFacilityChange]
 * @param {(value: string) => void} [props.onNotesChange]
 * @param {(value: string) => void} [props.onEstimatedDepartureDateChange]
 * @param {Record<string, string>} [props.fieldErrors]
 * @param {boolean} [props.showValidation]
 * @param {boolean} [props.sourceFacilityReadOnly] - When true, source is display-only (e.g. current login facility); `onSourceFacilityChange` is not used
 * @param {boolean} [props.sourceFacilityLoading] - Shown with read-only source while facility metadata is loading
 * @param {number | null} [props.lockedSourceFacilityId] - Logged-in facility id for read-only fallback label when the facility object is not in the catalog list
 */
export function FCTransferCreateInfoCard({
    destinationFacility = null,
    estimatedDepartureDate = '',
    fieldErrors = {},
    lockedSourceFacilityId = null,
    notes = '',
    onDestinationFacilityChange,
    onEstimatedDepartureDateChange,
    onNotesChange,
    onSourceFacilityChange,
    showValidation = false,
    sourceFacility = null,
    sourceFacilityLoading = false,
    sourceFacilityReadOnly = false,
}) {
    const notesId = useId();
    const dateId = useId();
    const showErrors = showValidation === true;

    const renderFieldError = useCallback(
        (field) => {
            const message = fieldErrors?.[field];
            if (!showErrors || !message) return null;
            return <p className="text-xs text-destructive">{message}</p>;
        },
        [fieldErrors, showErrors],
    );

    /** Matches SelectTrigger / Input control height and chrome for read-only source. */
    const readOnlyControlShellClassName =
        'flex min-h-9 w-full items-center rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs';

    const renderReadOnlySourceBody = () => {
        if (sourceFacilityLoading) {
            return <span className="text-muted-foreground">Loading source facility…</span>;
        }
        if (sourceFacility != null && typeof sourceFacility === 'object') {
            return (
                <span className="truncate font-medium text-foreground">
                    {String(sourceFacility.name ?? sourceFacility.id ?? '—')}
                </span>
            );
        }
        if (lockedSourceFacilityId != null) {
            return <span className="font-medium text-foreground">Facility #{lockedSourceFacilityId}</span>;
        }
        return (
            <span className="text-left text-destructive">
                No source facility on your account. You cannot create a transfer.
            </span>
        );
    };

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle className="text-base font-medium">Transfer information</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-4 sm:gap-y-4">
                    <div className="flex min-w-0 w-full max-w-full flex-col gap-2 sm:max-w-[17rem] sm:flex-[1_1_12rem]">
                        {sourceFacilityReadOnly ? (
                            <>
                                <span className={labelClassName}>
                                    Source FC <span className="text-destructive">*</span>
                                </span>
                                <div className={readOnlyControlShellClassName}>{renderReadOnlySourceBody()}</div>
                                {renderFieldError('sourceFacilityId')}
                            </>
                        ) : (
                            <>
                                <FacilityPickerV2
                                    label={
                                        <>
                                            Source FC <span className="text-destructive">*</span>
                                        </>
                                    }
                                    value={sourceFacility}
                                    onValueChange={onSourceFacilityChange}
                                />
                                {renderFieldError('sourceFacilityId')}
                            </>
                        )}
                    </div>

                    <div className="flex min-w-0 w-full max-w-full flex-col gap-2 sm:max-w-[17rem] sm:flex-[1_1_12rem]">
                        <FacilityPickerV2
                            label={
                                <>
                                    Destination FC <span className="text-destructive">*</span>
                                </>
                            }
                            value={destinationFacility}
                            onValueChange={onDestinationFacilityChange}
                        />
                        {renderFieldError('destinationFacilityId')}
                    </div>

                    <div className="flex w-full max-w-full flex-col gap-2 sm:min-w-[12.5rem] sm:max-w-[14rem] sm:shrink-0">
                        <Label
                            htmlFor={dateId}
                            className={`${labelClassName} inline-flex flex-wrap items-center gap-x-1`}
                        >
                            <span className="whitespace-nowrap">Estimated departure date</span>
                            <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id={dateId}
                            type="date"
                            value={estimatedDepartureDate}
                            onChange={(e) => onEstimatedDepartureDateChange?.(e.target.value)}
                        />
                        {renderFieldError('estimatedDepartureDate')}
                    </div>

                    <div className="flex min-w-0 w-full max-w-full flex-col gap-2 sm:min-w-[12rem] sm:max-w-[20rem] sm:flex-[1_1_14rem]">
                        <Label htmlFor={notesId} className={labelClassName}>
                            Notes
                        </Label>
                        <Textarea
                            id={notesId}
                            value={notes}
                            onChange={(e) => onNotesChange?.(e.target.value)}
                            placeholder="Optional notes for this transfer"
                            rows={1}
                            className="min-h-9 w-full resize-y py-2 text-sm [field-sizing:fixed]"
                        />
                        {renderFieldError('notes')}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

FCTransferCreateInfoCard.displayName = 'FCTransferCreateInfoCard';
